import { makeStyles } from '@mui/styles';
import { neptunePrimary, neutral7 } from 'apollo-react/colors';
import Button from 'apollo-react/components/Button';
import TableCell from 'apollo-react/components/TableCell';
import TableRow from 'apollo-react/components/TableRow';
import TextField from 'apollo-react/components/TextField';
import Drag from 'apollo-react-icons/Drag';
import classNames from 'classnames';
import _ from 'lodash';
import React, { useEffect, useRef, useState } from 'react';
import { useDrag, useDrop } from 'react-dnd';

import DateCell from '../../../components/CommonTableCells/DateCell';
import { useTranslation } from '../../../custom-hooks/useTranslation';

const ANIMATION_TIME = 1000;

const useStyles = makeStyles({
  root: {
    outline: '1px solid #fff',
  },
  dragIcon: {
    position: 'relative',
    top: 3,
    cursor: 'move',
  },
  moveToCell: {
    display: 'flex',
    gap: 16,
    alignItems: 'flex-start',
  },
  moveToField: {
    width: 90,
    margin: '8px 0',
    '& > div': {
      margin: 0,
    },
  },
  moveToButton: {
    marginTop: 8,
  },
  dragging: {
    visibility: 'hidden',
  },
  overCurrent: {
    position: 'relative',
    '&::after': {
      content: '""',
      position: 'absolute',
      bottom: 0,
      left: 0,
      display: 'block',
      width: '100%',
      height: 1,
      backgroundColor: neutral7,
    },
  },
  hoveredRow: {
    backgroundColor: 'rgba(0, 0, 0, 0.04)',
  },
  animatedRow: {
    animation: `$outlineColorFade ${ANIMATION_TIME / 1000}s ease-in`,
  },
  '@keyframes outlineColorFade': {
    from: {
      outlineColor: neptunePrimary,
    },
    to: {
      outlineColor: '#fff',
    },
  },
});

const DndRow = ({ rows, columns, index, setRows }) => {
  const classes = useStyles();
  const translateString = useTranslation();
  const ref = useRef(null);

  const [moveToValue, setMoveToValue] = useState('');
  const [dragIconPressed, setDragIconPressed] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [isRowMoved, setIsRowMoved] = useState(false);

  const currentRow = rows[index];
  const max = _.last(rows)?._printOrder + 1;

  useEffect(() => {
    if (isRowMoved) {
      setTimeout(() => {
        setIsRowMoved(false);
      }, ANIMATION_TIME);
    }
  }, [isRowMoved]);

  const [{ isDragging }, drag] = useDrag(
    () => ({
      type: 'row',
      item: { dragRow: currentRow },
      collect: (monitor) => ({
        isDragging: monitor.isDragging(),
      }),
      canDrag: () => dragIconPressed,
      end: (_, monitor) => {
        setDragIconPressed(false);
        monitor.didDrop() && setIsRowMoved(true);
      },
    }),
    [dragIconPressed, currentRow]
  );

  const [{ isOverCurrent }, drop] = useDrop(
    () => ({
      accept: 'row',
      drop: (item) => {
        if (item.dragRow._printOrder === currentRow._printOrder + 1) {
          return;
        }

        setRows((prevRows) => {
          const rowsWithoutDragRow = prevRows.filter((r) => r.key !== item.dragRow.key);
          const currentRowIndex = rowsWithoutDragRow.findIndex((r) => r.key === currentRow.key);
          rowsWithoutDragRow.splice(currentRowIndex + 1, 0, item.dragRow);

          return rowsWithoutDragRow.map((r, i) => ({ ...r, _printOrder: i }));
        });
      },
      collect: (monitor) => ({
        isOverCurrent: monitor.isOver(),
      }),
    }),
    [currentRow]
  );

  drag(drop(ref));

  const isValidNumber = (n) => Number.isInteger(n) && n > 0 && n <= max;

  const handleBlur = ({ target }) => {
    setTimeout(() => {
      if (target.value === '') {
        hasError && setHasError(false);
        return;
      }

      const newValue = +target.value;

      if (isValidNumber(newValue) && hasError) {
        setHasError(false);
        return;
      }

      if (!isValidNumber(newValue) && !hasError) {
        setHasError(true);
        return;
      }
    }, 100);
  };

  const handleClick = () => {
    if (!isValidNumber(+moveToValue) || moveToValue === '' || +moveToValue - 1 === index) {
      return;
    }

    setRows((prevRows) => {
      const rowsWithoutMovedRow = prevRows.filter((r) => r.key !== currentRow.key);
      rowsWithoutMovedRow.splice(+moveToValue - 1, 0, currentRow);

      return rowsWithoutMovedRow.map((r, i) => ({ ...r, _printOrder: i }));
    });
    setMoveToValue('');
    setIsRowMoved(true);
  };

  const getCellContent = (accessor) => {
    switch (accessor) {
      case 'rowNumber':
        return index + 1;

      case 'dragIcon':
        return rows.length > 1 ? (
          <Drag
            className={classes.dragIcon}
            fontSize="small"
            onMouseDown={() => setDragIconPressed(true)}
          />
        ) : null;

      case 'moveTo':
        return rows.length > 1 ? (
          <div className={classes.moveToCell}>
            <TextField
              className={classes.moveToField}
              size="small"
              type="number"
              placeholder={translateString('Move To')}
              helperText={hasError ? `Please enter an integer value between 1 and ${max}` : null}
              error={hasError}
              value={moveToValue}
              onChange={({ target }) => setMoveToValue(target.value)}
              onBlur={handleBlur}
            />
            <Button
              className={classes.moveToButton}
              variant="secondary"
              size="small"
              onClick={handleClick}
            >
              {translateString('Move')}
            </Button>
          </div>
        ) : null;

      case 'DBP_DOC_EFFECTIVE_DATE':
        return <DateCell row={currentRow} column={{ accessor: 'DBP_DOC_EFFECTIVE_DATE' }} />;

      default:
        return currentRow[accessor];
    }
  };

  return (
    <TableRow
      className={classNames({
        [classes.root]: true,
        [classes.animatedRow]: isRowMoved,
        [classes.dragging]: isDragging,
        [classes.overCurrent]: isOverCurrent,
        [classes.hoveredRow]: isHovered,
      })}
      ref={ref}
      onMouseOver={() => setIsHovered(true)}
      onMouseOut={() => setIsHovered(false)}
    >
      {columns.map(({ accessor }) => (
        <TableCell key={accessor}>{getCellContent(accessor)}</TableCell>
      ))}
    </TableRow>
  );
};

export default DndRow;
